import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../utils/apiClient';
import {
  LEGEND_FALLBACK,
  applyTheming,
  colorKeyForMode,
  hydrateActivityMap,
  mapActivitiesToDbIds,
  missingActivities,
  serializeActivityMap,
} from './progressTheming';
import {
  clearLocalActivityMap,
  normalizeApsUrn,
  readLocalActivityMap,
  writeLocalActivityMap,
} from './activityMapCache';
import {
  activityMatchesFilters,
  collectDbIds,
  lookAheadWindow,
} from './activityListFilters';

const VIEWER_VERSION = '7.98';
const STYLE_URL = `https://developer.api.autodesk.com/modelderivative/v2/viewers/${VIEWER_VERSION}/style.min.css`;
const SCRIPT_URL = `https://developer.api.autodesk.com/modelderivative/v2/viewers/${VIEWER_VERSION}/viewer3D.min.js`;

let viewerAssetsPromise = null;

function loadViewerAssets() {
  if (window.Autodesk?.Viewing) return Promise.resolve();
  if (viewerAssetsPromise) return viewerAssetsPromise;

  viewerAssetsPromise = new Promise((resolve, reject) => {
    if (!document.querySelector(`link[href="${STYLE_URL}"]`)) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = STYLE_URL;
      document.head.appendChild(link);
    }
    const existing = document.querySelector(`script[src="${SCRIPT_URL}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', reject);
      if (window.Autodesk?.Viewing) resolve();
      return;
    }
    const script = document.createElement('script');
    script.src = SCRIPT_URL;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Autodesk Viewer'));
    document.body.appendChild(script);
  });

  return viewerAssetsPromise;
}

function formatDay(iso) {
  if (!iso) return '—';
  return String(iso).slice(0, 10);
}

/**
 * Fullscreen Autodesk APS Viewer with optional live P6 progress colouring.
 */
const ApsModelViewer = ({
  urn,
  accessToken,
  expiresIn = 3600,
  title,
  jobNumber,
  onClose,
  onTokenRefresh,
}) => {
  const { instance, accounts } = useMsal();
  const containerRef = useRef(null);
  const viewerRef = useRef(null);
  const mapRef = useRef(new Map());
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modelReady, setModelReady] = useState(false);
  const [progress, setProgress] = useState(null);
  const [progressError, setProgressError] = useState(null);
  const [rev, setRev] = useState('');
  const [percentSource, setPercentSource] = useState('p6');
  const [mode, setMode] = useState('status');
  const [asOf, setAsOf] = useState('');
  const [matching, setMatching] = useState(null);
  const [matchStats, setMatchStats] = useState(null);
  const [matchMeta, setMatchMeta] = useState(null);
  const [query, setQuery] = useState('');
  const [colorKeys, setColorKeys] = useState([]);
  const [lookAheadOn, setLookAheadOn] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const progressRef = useRef(progress);
  const matchRunRef = useRef(0);
  progressRef.current = progress;

  const loadProgress = useCallback(async (revParam, source = percentSource) => {
    if (!jobNumber) return;
    try {
      setProgressError(null);
      const qs = new URLSearchParams();
      if (revParam) qs.set('rev', revParam);
      if (source === 'tracker') qs.set('percentSource', 'tracker');
      const q = qs.toString() ? `?${qs.toString()}` : '';
      const res = await authenticatedApiCall(instance, accounts, 'GET', `/schedule-4d/${jobNumber}${q}`);
      const data = res.data?.data;
      setProgress(data || null);
      if (data?.selectedRev) setRev(String(data.selectedRev));
      setAsOf((prev) => prev || data?.dataDate || data?.plannedRange?.end || '');
    } catch (err) {
      setProgressError(err.response?.data?.error || err.message || 'Failed to load P6 progress');
    }
  }, [instance, accounts, jobNumber]);

  useEffect(() => {
    loadProgress();
  }, [loadProgress]);

  useEffect(() => {
    let cancelled = false;
    let viewer = null;

    const run = async () => {
      try {
        setLoading(true);
        setError(null);
        setModelReady(false);
        await loadViewerAssets();
        if (cancelled || !containerRef.current) return;

        const Autodesk = window.Autodesk;
        const options = {
          env: 'AutodeskProduction2',
          api: 'streamingV2',
          getAccessToken: (onTokenReady) => {
            const deliver = (token, exp) => onTokenReady(token, exp || 3600);
            if (onTokenRefresh) {
              Promise.resolve(onTokenRefresh())
                .then((t) => deliver(t.accessToken || t, t.expiresIn || 3600))
                .catch(() => deliver(accessToken, expiresIn));
            } else {
              deliver(accessToken, expiresIn);
            }
          }
        };

        await new Promise((resolve) => Autodesk.Viewing.Initializer(options, resolve));
        if (cancelled || !containerRef.current) return;

        viewer = new Autodesk.Viewing.GuiViewer3D(containerRef.current, {
          extensions: ['Autodesk.DocumentBrowser']
        });
        const started = viewer.start();
        if (started > 0) throw new Error('Viewer failed to start');
        viewerRef.current = viewer;

        const documentId = urn.startsWith('urn:') ? urn : `urn:${urn}`;
        await new Promise((resolve, reject) => {
          Autodesk.Viewing.Document.load(
            documentId,
            (doc) => {
              const defaultModel = doc.getRoot().getDefaultGeometry();
              if (!defaultModel) {
                reject(new Error('No 3D view found in this model'));
                return;
              }
              viewer
                .loadDocumentNode(doc, defaultModel)
                .then(() => resolve())
                .catch(reject);
            },
            (code, message, errors) => {
              reject(new Error(message || `Document load failed (${code})`));
              console.error('APS Document.load errors:', errors);
            }
          );
        });

        if (!cancelled) {
          setLoading(false);
          setModelReady(true);
        }
      } catch (err) {
        console.error('ApsModelViewer error:', err);
        if (!cancelled) {
          setError(err.message || 'Failed to open model');
          setLoading(false);
        }
      }
    };

    run();

    return () => {
      cancelled = true;
      if (viewerRef.current) {
        try {
          viewerRef.current.finish();
        } catch (_) { /* ignore */ }
        viewerRef.current = null;
      }
    };
  }, [urn, accessToken, expiresIn, onTokenRefresh]);

  const colorNow = useCallback((nextMode = mode, nextAsOf = asOf) => {
    const viewer = viewerRef.current;
    const THREE = window.THREE;
    if (!viewer || !progress?.activities?.length) return;
    const stats = applyTheming(
      viewer,
      THREE,
      progress.activities,
      mapRef.current,
      progress.legend || LEGEND_FALLBACK,
      nextMode,
      nextAsOf || progress.dataDate
    );
    setMatchStats(stats);
  }, [progress, mode, asOf]);

  const persistMap = useCallback(async (byActivity, { complete }) => {
    if (!jobNumber || !urn) return;
    writeLocalActivityMap(jobNumber, urn, byActivity, { complete });
    try {
      await authenticatedApiCall(
        instance,
        accounts,
        'PUT',
        `/schedule-4d/${encodeURIComponent(jobNumber)}/activity-map`,
        {
          urn,
          activityMap: serializeActivityMap(byActivity),
          complete: complete === true,
        }
      );
    } catch (_) {
      // Local copy still skips the next Open on this machine.
    }
  }, [instance, accounts, jobNumber, urn]);

  const applySavedOrMatch = useCallback(async ({ force = false } = {}) => {
    const viewer = viewerRef.current;
    const progressNow = progressRef.current;
    if (!viewer || !progressNow?.activities?.length || !jobNumber) return;
    const run = ++matchRunRef.current;
    const all = (progressNow.activities || []).filter((a) => a.activityId);

    let byActivity = new Map();
    if (force) {
      clearLocalActivityMap(jobNumber, urn);
      setMatchMeta(null);
    } else {
      let payload = null;
      try {
        const res = await authenticatedApiCall(
          instance,
          accounts,
          'GET',
          `/schedule-4d/${encodeURIComponent(jobNumber)}/activity-map?urn=${encodeURIComponent(normalizeApsUrn(urn))}`
        );
        payload = res.data?.data || null;
      } catch (_) {
        payload = null;
      }
      if (payload?.activityMap && Object.keys(payload.activityMap).length) {
        byActivity = hydrateActivityMap(payload.activityMap);
        setMatchMeta({
          source: 'saved',
          complete: payload.complete === true,
          updatedAt: payload.updatedAt || null,
        });
      } else {
        const local = readLocalActivityMap(jobNumber, urn);
        if (local.found) {
          byActivity = local.map;
          setMatchMeta({
            source: 'local',
            complete: local.complete,
            updatedAt: local.updatedAt,
          });
        }
      }
    }

    if (run !== matchRunRef.current) return;
    mapRef.current = byActivity;
    if (byActivity.size) colorNow();

    const toScan = force ? all : missingActivities(all, byActivity);
    if (!toScan.length) {
      setMatching(null);
      setMatchMeta((prev) => ({ source: prev?.source || 'saved', complete: true, updatedAt: prev?.updatedAt || null }));
      return;
    }

    setMatching({ done: all.length - toScan.length, total: all.length });
    const result = await mapActivitiesToDbIds(
      viewer,
      force ? all : toScan,
      progressNow.searchPropertyNames,
      (done, total) => {
        if (run !== matchRunRef.current) return;
        setMatching({ done, total });
      },
      {
        into: force ? new Map() : byActivity,
        skipExisting: !force,
        progressTotal: all.length,
        partialEvery: 25,
        onPartial: async (partial) => {
          if (run !== matchRunRef.current) return;
          mapRef.current = partial;
          colorNow();
          persistMap(partial, { complete: false });
        },
      }
    );
    if (run !== matchRunRef.current) return;
    mapRef.current = result;
    setMatching(null);
    colorNow();
    await persistMap(result, { complete: true });
    setMatchMeta({ source: 'saved', complete: true, updatedAt: new Date().toISOString() });
  }, [jobNumber, urn, instance, accounts, colorNow, persistMap]);

  useEffect(() => {
    if (!modelReady || !progress?.available || !jobNumber) return undefined;
    applySavedOrMatch({ force: false });
    return () => { matchRunRef.current += 1; };
    // Match is per model URN, not P6 rev / Tracker %. Those only recolour.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelReady, progress?.available, jobNumber, urn]);

  useEffect(() => {
    if (!modelReady || !mapRef.current.size) return;
    colorNow();
  }, [mode, asOf, colorNow, modelReady]);

  const isolateActivity = (activityId) => {
    const viewer = viewerRef.current;
    const ids = mapRef.current.get(activityId) || [];
    setSelectedId(activityId);
    if (!viewer) return;
    if (!ids.length) {
      viewer.isolate([-1]);
      return;
    }
    viewer.isolate(ids);
    viewer.fitToView(ids);
  };

  const clearFilters = () => {
    setQuery('');
    setColorKeys([]);
    setLookAheadOn(false);
    setSelectedId(null);
    viewerRef.current?.isolate([]);
  };

  const toggleColor = (key) => {
    setSelectedId(null);
    setColorKeys((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  };

  const legend = progress?.legend || LEGEND_FALLBACK;
  const fromDate = progress?.dataDate || asOf || null;
  const lookWin = lookAheadOn ? lookAheadWindow(fromDate, 3) : null;
  const filtersActive = Boolean(query.trim()) || colorKeys.length > 0 || lookAheadOn;

  const activities = useMemo(() => {
    const list = progress?.activities || [];
    return list.filter((a) => activityMatchesFilters(a, {
      query,
      colorKeys,
      lookAheadWeeks: lookAheadOn ? 3 : 0,
      fromDate: progress?.dataDate || asOf || null,
      mode,
      asOf: asOf || progress?.dataDate,
      isMapped: (act) => (mapRef.current.get(act.activityId) || []).length > 0,
    }));
  }, [progress, query, colorKeys, lookAheadOn, mode, asOf, matchStats, matching]);

  const filterFitKey = `${colorKeys.join(',')}|${lookAheadOn}|${query}`;
  const lastFitKeyRef = useRef('');

  useEffect(() => {
    const viewer = viewerRef.current;
    if (!viewer || !modelReady) return;
    if (!filtersActive) {
      lastFitKeyRef.current = '';
      if (!selectedId) viewer.isolate([]);
      return;
    }
    if (matchStats && matchStats.mapped === 0) return;
    const ids = collectDbIds(activities, mapRef.current);
    if (ids.length) {
      viewer.isolate(ids);
      if ((colorKeys.length || lookAheadOn) && lastFitKeyRef.current !== filterFitKey) {
        viewer.fitToView(ids);
        lastFitKeyRef.current = filterFitKey;
      }
    } else {
      viewer.isolate([-1]);
    }
  }, [modelReady, filtersActive, activities, colorKeys, lookAheadOn, selectedId, matchStats, matching, filterFitKey]);

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 12000,
        background: '#0f172a',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '10px 14px',
          background: '#0f766e',
          color: '#fff',
          flexShrink: 0
        }}
      >
        <strong style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {title || '3D Model'}
        </strong>
        <span style={{ fontSize: 12, opacity: 0.85 }}>
          {percentSource === 'tracker' ? 'Tracker colour' : 'P6 colour'} · orbit / pan / section
        </span>
        <button type="button" onClick={onClose} style={headerBtn}>
          Close
        </button>
      </div>

      {error && (
        <div style={{ background: '#7f1d1d', color: '#fecaca', padding: '10px 14px' }}>
          {error}
        </div>
      )}

      <div style={{ flex: 1, minHeight: 0, display: 'flex' }}>
        <div style={{ flex: 1, minWidth: 0, position: 'relative' }}>
          {loading && !error && (
            <div style={{ color: '#94a3b8', padding: 16, fontSize: 14, position: 'absolute', zIndex: 2 }}>
              Loading Autodesk Viewer…
            </div>
          )}
          <div ref={containerRef} style={{ position: 'absolute', inset: 0 }} />
        </div>

        <aside style={dock}>
          <div style={{ fontWeight: 700, color: '#0f172a', marginBottom: 6 }}>Progress colour</div>
          <div style={{ fontSize: 12, color: '#64748b', marginBottom: 10, lineHeight: 1.45 }}>
            {percentSource === 'tracker'
              ? 'Colours use the hours-weighted tracker rollup (same Activity ID as the field trackers). Activities with no tracker rows stay slate / late vs planned finish. P6 % is still shown on each row.'
              : 'Colours come from live Primavera physical %. Switch to Tracker % to see the field rollup on the same Activity IDs.'}
            {' '}Planning slider uses planned start / finish only.
          </div>

          <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
            <button
              type="button"
              onClick={() => {
                setPercentSource('p6');
                loadProgress(rev, 'p6');
              }}
              style={percentSource === 'p6' ? tabOn : tabOff}
            >
              P6 physical %
            </button>
            <button
              type="button"
              onClick={() => {
                setPercentSource('tracker');
                loadProgress(rev, 'tracker');
              }}
              style={percentSource === 'tracker' ? tabOn : tabOff}
            >
              Tracker %
            </button>
          </div>

          {progressError && (
            <div style={{ background: '#fef2f2', color: '#991b1b', padding: 8, borderRadius: 6, fontSize: 12, marginBottom: 10 }}>
              {progressError}
            </div>
          )}

          {progress && !progress.available && (
            <div style={{ background: '#fffbeb', color: '#92400e', padding: 8, borderRadius: 6, fontSize: 12, marginBottom: 10 }}>
              {progress.reason || 'No live P6 for this job yet.'}
            </div>
          )}

          {progress?.revisions?.length > 0 && (
            <label style={field}>
              Rev
              <select
                value={rev}
                onChange={(e) => {
                  setRev(e.target.value);
                  loadProgress(e.target.value, percentSource);
                }}
                style={select}
              >
                {progress.revisions.map((r) => (
                  <option key={r.id || r.projId} value={r.id || r.projId}>
                    {r.label || r.projShortName}
                  </option>
                ))}
              </select>
            </label>
          )}

          <div style={{ display: 'flex', gap: 6, margin: '8px 0 10px' }}>
            <button
              type="button"
              onClick={() => setMode('status')}
              style={mode === 'status' ? tabOn : tabOff}
            >
              Status
            </button>
            <button
              type="button"
              onClick={() => setMode('planned')}
              style={mode === 'planned' ? tabOn : tabOff}
            >
              Planned 4D
            </button>
          </div>

          {mode === 'planned' && (
            <label style={field}>
              As of (planned dates)
              <input
                type="date"
                min={progress?.plannedRange?.start || undefined}
                max={progress?.plannedRange?.end || undefined}
                value={asOf || progress?.dataDate || ''}
                onChange={(e) => setAsOf(e.target.value)}
                style={select}
              />
            </label>
          )}

          <div style={{ fontSize: 11, color: '#64748b', margin: '4px 0 6px', lineHeight: 1.4 }}>
            Click a colour to keep those pieces. Several colours are an OR. 3-week look-ahead keeps work that overlaps the data date plus 21 days, plus late remaining work (complete stays out).
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, margin: '4px 0 8px' }}>
            {legend.filter((l) => l.key !== 'unmapped' || (matchStats && matchStats.unmapped > 0)).map((l) => {
              const on = colorKeys.includes(l.key);
              const dim = colorKeys.length > 0 && !on;
              return (
                <button
                  key={l.key}
                  type="button"
                  aria-pressed={on}
                  onClick={() => toggleColor(l.key)}
                  style={{
                    ...chip,
                    background: l.hex,
                    boxShadow: on ? '0 0 0 2px #0f172a' : 'none',
                    opacity: dim ? 0.4 : 1,
                  }}
                >
                  {l.label}
                </button>
              );
            })}
          </div>

          <button
            type="button"
            aria-pressed={lookAheadOn}
            onClick={() => {
              setSelectedId(null);
              setLookAheadOn((prev) => !prev);
            }}
            style={lookAheadOn ? lookAheadOnBtn : lookAheadOffBtn}
          >
            3-week look-ahead
            {lookWin ? ` · ${formatDay(lookWin.start)} → ${formatDay(lookWin.end)}` : ''}
          </button>

          <div style={{ fontSize: 12, color: '#475569', margin: '8px 0' }}>
            {progress?.p6Project ? `${progress.p6Project} · ` : ''}
            data date {formatDay(progress?.dataDate)}
            {progress?.activities?.length
              ? ` · ${activities.length} of ${progress.activities.length} activities`
              : ''}
            {percentSource === 'tracker' && progress?.available
              ? ` · tracker on ${progress.trackerMatched || 0}`
              : ''}
          </div>

          {matching && (
            <div style={{ fontSize: 12, color: '#0f766e', marginBottom: 8 }}>
              Matching Activity IDs… {matching.done}/{matching.total}
              {matching.done > 0 ? ' — already-matched IDs stay painted' : ''}
            </div>
          )}

          {!matching && matchMeta && (
            <div style={{ fontSize: 12, color: '#0f766e', marginBottom: 8 }}>
              {matchMeta.complete ? 'Saved match' : 'Saved match (partial)'}
              {matchMeta.updatedAt ? ` · ${formatDay(matchMeta.updatedAt)}` : ''}
              {matchMeta.source === 'local' ? ' · this browser' : ''}
            </div>
          )}

          {matchStats && (
            <div style={{ fontSize: 12, color: '#334155', marginBottom: 8 }}>
              Mapped {matchStats.mapped} · no model match {matchStats.unmapped}
              {matchStats.mapped === 0 ? (
                <div style={{ color: '#92400e', marginTop: 4 }}>
                  This translation has no Activity ID on items. Colour stays off so we do not grey the whole plant.
                  Download for YR Tools / stamp Activity ID, then reopen.
                </div>
              ) : null}
            </div>
          )}

          <button
            type="button"
            onClick={() => applySavedOrMatch({ force: true })}
            disabled={!progress?.available || !!matching}
            style={refreshBtn}
          >
            {matching ? 'Matching…' : 'Refresh matching'}
          </button>
          <button
            type="button"
            onClick={clearFilters}
            style={{ ...refreshBtn, background: '#fff', color: '#334155', border: '1px solid #cbd5e1', marginTop: 6 }}
          >
            Show all
          </button>

          <input
            type="search"
            placeholder="Filter Activity ID or name"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            style={{ ...select, marginTop: 10 }}
          />

          <div style={{ flex: 1, overflow: 'auto', marginTop: 8, borderTop: '1px solid #e2e8f0' }}>
            {activities.slice(0, 400).map((a) => {
              const key = colorKeyForMode(a, mode, asOf || progress?.dataDate);
              const hex = (legend.find((l) => l.key === key) || {}).hex || '#94a3b8';
              const mapped = (mapRef.current.get(a.activityId) || []).length > 0;
              return (
                <button
                  key={a.activityId}
                  type="button"
                  onClick={() => isolateActivity(a.activityId)}
                  style={{
                    ...actRow,
                    outline: selectedId === a.activityId ? '2px solid #0f766e' : 'none',
                    opacity: matchStats && !mapped ? 0.55 : 1
                  }}
                >
                  <span style={{ ...dot, background: hex }} />
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, color: '#0f172a' }}>{a.activityId}</div>
                    <div style={{ color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {a.name}
                    </div>
                    <div style={{ color: '#94a3b8' }}>
                      {percentSource === 'tracker'
                        ? `T ${a.trackerPercentComplete == null ? '—' : `${Math.round(a.trackerPercentComplete)}%`} · P6 ${Math.round(a.p6PercentComplete || 0)}%`
                        : `${Math.round(a.percentComplete || 0)}%`}
                      {' · '}{formatDay(a.plannedStart)} → {formatDay(a.plannedFinish)}
                      {mapped ? '' : ' · no match'}
                    </div>
                  </span>
                </button>
              );
            })}
          </div>
        </aside>
      </div>
    </div>
  );
};

const headerBtn = {
  background: 'rgba(255,255,255,0.15)',
  border: '1px solid rgba(255,255,255,0.35)',
  color: '#fff',
  borderRadius: 8,
  padding: '6px 12px',
  cursor: 'pointer',
  fontSize: 13
};

const dock = {
  width: 340,
  flexShrink: 0,
  background: '#f8fafc',
  borderLeft: '1px solid #e2e8f0',
  padding: 12,
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0
};

const field = { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: '#334155', fontWeight: 600 };
const select = {
  width: '100%',
  border: '1px solid #cbd5e1',
  borderRadius: 6,
  padding: '6px 8px',
  fontSize: 13,
  fontWeight: 400
};
const tabOn = {
  flex: 1,
  border: 'none',
  background: '#0f766e',
  color: '#fff',
  borderRadius: 6,
  padding: '6px 8px',
  fontSize: 12,
  cursor: 'pointer'
};
const tabOff = {
  ...tabOn,
  background: '#e2e8f0',
  color: '#334155'
};
const chip = {
  color: '#fff',
  fontSize: 10,
  fontWeight: 700,
  padding: '3px 8px',
  borderRadius: 999,
  border: 'none',
  cursor: 'pointer',
};
const lookAheadOnBtn = {
  width: '100%',
  border: 'none',
  background: '#0f766e',
  color: '#fff',
  borderRadius: 8,
  padding: '7px 10px',
  fontSize: 12,
  fontWeight: 600,
  cursor: 'pointer',
  textAlign: 'left',
};
const lookAheadOffBtn = {
  ...lookAheadOnBtn,
  background: '#e2e8f0',
  color: '#334155',
};
const refreshBtn = {
  width: '100%',
  border: 'none',
  background: '#0f766e',
  color: '#fff',
  borderRadius: 8,
  padding: '8px 10px',
  fontSize: 13,
  cursor: 'pointer'
};
const actRow = {
  width: '100%',
  display: 'flex',
  gap: 8,
  textAlign: 'left',
  background: '#fff',
  border: 'none',
  borderBottom: '1px solid #f1f5f9',
  padding: '8px 4px',
  cursor: 'pointer',
  fontSize: 12
};
const dot = { width: 10, height: 10, borderRadius: 99, marginTop: 4, flexShrink: 0 };

export default ApsModelViewer;
